import type { ActivityEvent, Lead, LeadAttachment, Membership, Task } from "../../lib/types";
import type { AutomationIncident } from "../../lib/command-contract";
import type { CommunicationThread } from "../communication/contracts";
import {
  invoiceBalanceCents,
  type FinanceInvoice,
  type FinanceReceipt,
  type RevenueGoal,
} from "../finance/contracts";

/**
 * Feed de notificações — o que exige atenção agora, derivado do domínio.
 *
 * Três decisões organizam este módulo:
 *
 * - **O aviso é derivado, não armazenado.** Nada aqui cria uma linha nova ao
 *   observar uma tarefa vencida; a fonte continua sendo a tarefa. O que é
 *   persistido é apenas o que o usuário fez com o aviso — leu, resolveu — em
 *   `notification_states`, endereçado por uma chave estável.
 * - **A chave carrega o motivo, não o instante.** `tarefa-vencida:<id>:<prazo>`
 *   sobrevive a recarregar a página e a reabrir o app amanhã, mas muda quando o
 *   prazo muda: remarcar uma tarefa é um fato novo e volta a pedir atenção.
 *   Uma chave com a hora corrente reapareceria como não lida a cada render.
 * - **Ler não resolve.** Um aviso lido continua pendente; resolver é uma ação
 *   explícita, e resolver não conserta a causa — a tarefa continua vencida até
 *   ser concluída. As duas marcas são independentes de propósito (§18).
 *
 * As regras são funções puras sobre listas. Cada uma tem teste próprio e recebe
 * o dia civil por parâmetro, nunca lê o relógio (§7.6).
 */

/**
 * A ordem é a da trilha do protótipo — Funis, CosmosAI, Mensagens, Cadastros,
 * Anexos —, seguida das duas caixas que o nosso domínio acrescenta e o
 * protótipo dobrava dentro de "Funis": tarefa vencida e fatura atrasada.
 */
export const NOTICE_CATEGORIES = [
  "automacao",
  "ia",
  "mensagem",
  "lead",
  "anexo",
  "tarefa",
  "financeiro",
] as const;
export type NoticeCategory = (typeof NOTICE_CATEGORIES)[number];

export const NOTICE_CATEGORY_LABEL: Record<NoticeCategory, string> = {
  tarefa: "Tarefas",
  financeiro: "Financeiro",
  // O protótipo chama de "CosmosAI" a projeção sobre a meta, e é o detalhe do
  // aviso que diz de onde ela vem: conta fechada, não modelo (§24, §3).
  ia: "CosmosAI",
  // "Cadastros" e "Funis" são as palavras do protótipo para o que muda o
  // estado do lead e para o que vem da automação do funil (§24).
  lead: "Cadastros",
  mensagem: "Mensagens",
  automacao: "Funis",
  anexo: "Anexos",
};

/** Gravidade do aviso; ordena o feed junto com a data. */
export type NoticeSeverity = "critico" | "atencao" | "informativo";

const SEVERITY_WEIGHT: Record<NoticeSeverity, number> = {
  critico: 0,
  atencao: 1,
  informativo: 2,
};

export interface Notice {
  /** Identidade estável do aviso; é o que `notification_states` endereça. */
  key: string;
  category: NoticeCategory;
  severity: NoticeSeverity;
  title: string;
  detail: string;
  /** Instante que ordena o feed, em UTC. */
  occurredAt: string;
  /**
   * Verdadeiro quando existe uma pendência real a tratar. Um aviso informativo
   * não oferece "resolver": não há o que confirmar.
   */
  actionable: boolean;
  leadId?: string;
  funnelId?: string;
  taskId?: string;
  invoiceId?: string;
  threadId?: string;
  incidentId?: string;
  readAt?: string;
  resolvedAt?: string;
}

/** Marca persistida do que o usuário fez com um aviso. */
export interface NoticeState {
  noticeKey: string;
  readAt?: string;
  resolvedAt?: string;
}

/** Dias sem interação real que classificam um lead como parado (§7.7). */
export const COOLING_THRESHOLDS = [14, 30] as const;

/** Antecedência em que uma fatura a vencer passa a aparecer no feed. */
export const INVOICE_DUE_SOON_DAYS = 3;

const DAY_MS = 86_400_000;

/**
 * Formatadores por fuso, criados uma vez.
 *
 * Construir um `Intl.DateTimeFormat` custa caro — cerca de meio milissegundo — e
 * `civilDate` é chamada uma vez por lead, por tarefa e por fatura a cada
 * montagem do feed. Com 5.000 cadastros a construção sozinha levava 3 segundos
 * e travava a troca de página da lista, porque o sino remonta o feed junto.
 * Formatar é barato; criar o formatador é que não era.
 */
const DATE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function dateFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = DATE_FORMATTERS.get(timeZone);
  if (cached) return cached;
  const created = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  DATE_FORMATTERS.set(timeZone, created);
  return created;
}

/**
 * Dia civil de um instante no fuso do usuário.
 *
 * Um vencimento às 22h de Brasília é 01h do dia seguinte em UTC: comparar o
 * prefixo do ISO marcaria como vencida uma tarefa que ainda tem o dia inteiro.
 */
export function civilDate(instant: string, timeZone = "America/Sao_Paulo"): string {
  const moment = new Date(instant);
  if (Number.isNaN(moment.getTime())) return "";
  return dateFormatter(timeZone).format(moment);
}

/** Dias inteiros entre duas datas civis; negativo quando `to` é anterior. */
export function daysBetween(from: string, to: string): number {
  const start = Date.parse(`${from}T12:00:00Z`);
  const end = Date.parse(`${to}T12:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.round((end - start) / DAY_MS);
}

export interface FeedInput {
  /** Dia civil corrente do usuário (`YYYY-MM-DD`). */
  today: string;
  timeZone?: string;
  leads: readonly Lead[];
  memberships: readonly Membership[];
  tasks: readonly Task[];
  invoices?: readonly FinanceInvoice[];
  receipts?: readonly FinanceReceipt[];
  revenueGoals?: readonly RevenueGoal[];
  threads?: readonly CommunicationThread[];
  incidents?: readonly AutomationIncident[];
  attachments?: readonly LeadAttachment[];
  events?: readonly ActivityEvent[];
  states?: readonly NoticeState[];
  /** Categorias silenciadas pelo usuário; ausente significa todas ativas. */
  mutedCategories?: readonly NoticeCategory[];
  /** Inclui os avisos já resolvidos, para a aba de histórico. */
  includeResolved?: boolean;
}

function leadLabel(lead: Lead | undefined, fallback = "Lead removido"): string {
  if (!lead) return fallback;
  return lead.company ? `${lead.name} · ${lead.company}` : lead.name;
}

/**
 * A quem a tarefa pertence. Tarefa sem cadastro é interna (§7.5) e não é um
 * lead que sumiu: dizer "Lead removido" ali seria acusar uma exclusão que não
 * aconteceu. É o rótulo "Interna" que o quadro de tarefas já usa.
 */
function taskOwnerLabel(task: Task, lead: Lead | undefined): string {
  return task.leadId ? leadLabel(lead) : "Interna";
}

function currency(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/**
 * Tarefas pendentes vencidas e as de hoje.
 *
 * Uma tarefa concluída sai do feed pela causa, não por "resolver": o aviso
 * deixa de ser derivado assim que a tarefa muda de estado.
 */
/**
 * Funil da participação do lead.
 *
 * O aviso nasce de uma tarefa ou de uma fatura, que não carregam funil. Quem
 * sabe o funil é a participação — e sem ele o filtro por funil da central não
 * teria como separar nada.
 */
function funnelIndex(input: FeedInput): Map<string, string> {
  const index = new Map<string, string>();
  for (const membership of input.memberships) {
    if (!index.has(membership.leadId)) index.set(membership.leadId, membership.funnelId);
  }
  return index;
}

export function taskNotices(input: FeedInput): Notice[] {
  const zone = input.timeZone;
  const byLead = new Map(input.leads.map((lead) => [lead.id, lead]));
  const funnels = funnelIndex(input);
  const notices: Notice[] = [];
  for (const task of input.tasks) {
    if (task.status !== "pending") continue;
    const due = civilDate(task.dueAt, zone);
    if (!due) continue;
    const lead = task.leadId ? byLead.get(task.leadId) : undefined;
    if (lead?.archived) continue;
    const late = daysBetween(due, input.today);
    if (late > 0) {
      notices.push({
        key: `tarefa-vencida:${task.id}:${due}`,
        category: "tarefa",
        severity: "critico",
        title: `Tarefa vencida há ${late} ${late === 1 ? "dia" : "dias"}`,
        detail: `${task.description} · ${taskOwnerLabel(task, lead)}`,
        occurredAt: task.dueAt,
        actionable: true,
        leadId: task.leadId,
        funnelId: task.membershipId
          ? input.memberships.find((item) => item.id === task.membershipId)?.funnelId
          : task.leadId
            ? funnels.get(task.leadId)
            : undefined,
        taskId: task.id,
      });
    } else if (late === 0) {
      notices.push({
        key: `tarefa-hoje:${task.id}:${due}`,
        category: "tarefa",
        severity: "atencao",
        title: "Tarefa para hoje",
        detail: `${task.description} · ${taskOwnerLabel(task, lead)}`,
        occurredAt: task.dueAt,
        actionable: true,
        leadId: task.leadId,
        funnelId: task.membershipId
          ? input.memberships.find((item) => item.id === task.membershipId)?.funnelId
          : task.leadId
            ? funnels.get(task.leadId)
            : undefined,
        taskId: task.id,
      });
    }
  }
  return notices;
}

/**
 * Faturas atrasadas e a vencer.
 *
 * Fatura cancelada ou já quitada não gera aviso; parcial atrasada gera, com o
 * saldo restante, que é o número que o usuário precisa cobrar.
 */
export function invoiceNotices(input: FeedInput): Notice[] {
  const invoices = input.invoices ?? [];
  if (invoices.length === 0) return [];
  const receipts = input.receipts ?? [];
  const byLead = new Map(input.leads.map((lead) => [lead.id, lead]));
  const funnels = funnelIndex(input);
  const notices: Notice[] = [];
  for (const invoice of invoices) {
    if (invoice.cancelledAt) continue;
    const balance = invoiceBalanceCents(invoice, receipts);
    if (balance <= 0) continue;
    const lead = byLead.get(invoice.leadId);
    if (lead?.archived) continue;
    const late = daysBetween(invoice.dueOn, input.today);
    if (late > 0) {
      notices.push({
        key: `fatura-atrasada:${invoice.id}:${invoice.dueOn}`,
        category: "financeiro",
        severity: "critico",
        title: `Recebimento atrasado há ${late} ${late === 1 ? "dia" : "dias"}`,
        detail: `${currency(balance)} em aberto · ${leadLabel(lead)}`,
        occurredAt: `${invoice.dueOn}T12:00:00.000Z`,
        actionable: true,
        leadId: invoice.leadId,
        funnelId: funnels.get(invoice.leadId),
        invoiceId: invoice.id,
      });
    } else if (late >= -INVOICE_DUE_SOON_DAYS) {
      notices.push({
        key: `fatura-a-vencer:${invoice.id}:${invoice.dueOn}`,
        category: "financeiro",
        severity: "atencao",
        title: late === 0 ? "Recebimento vence hoje" : `Recebimento vence em ${-late} dia(s)`,
        detail: `${currency(balance)} · ${leadLabel(lead)}`,
        occurredAt: `${invoice.dueOn}T12:00:00.000Z`,
        actionable: true,
        leadId: invoice.leadId,
        funnelId: funnels.get(invoice.leadId),
        invoiceId: invoice.id,
      });
    }
  }
  return notices;
}

/**
 * Leads em negociação sem interação real há muito tempo.
 *
 * A chave carrega a faixa (14 ou 30 dias), não a contagem: sem isso o aviso
 * voltaria a aparecer como novo todo dia e a marca de leitura nunca valeria.
 * Mover de etapa ou editar cadastro NÃO conta como interação (§7.7); quando o
 * lead nunca teve contato registrado, o aviso diz isso em vez de fingir que a
 * data de cadastro é uma conversa.
 */
export function coolingLeadNotices(input: FeedInput): Notice[] {
  const zone = input.timeZone;
  const active = new Map<string, Membership>();
  for (const membership of input.memberships) {
    if (membership.status === "active") active.set(membership.leadId, membership);
  }
  const notices: Notice[] = [];
  for (const lead of input.leads) {
    if (lead.archived) continue;
    const membership = active.get(lead.id);
    if (!membership) continue;
    const reference = lead.lastInteractionAt ?? lead.createdAt;
    const referenceDate = civilDate(reference, zone);
    if (!referenceDate) continue;
    const idle = daysBetween(referenceDate, input.today);
    const threshold = [...COOLING_THRESHOLDS]
      .sort((left, right) => right - left)
      .find((limit) => idle >= limit);
    if (threshold === undefined) continue;
    notices.push({
      key: `lead-parado:${lead.id}:${threshold}`,
      category: "lead",
      severity: threshold >= 30 ? "critico" : "atencao",
      title: `Sem interação há ${idle} dias`,
      detail: lead.lastInteractionAt
        ? `${leadLabel(lead)} · última interação em ${referenceDate}`
        : `${leadLabel(lead)} · nenhuma interação registrada desde o cadastro`,
      occurredAt: reference,
      actionable: true,
      leadId: lead.id,
      funnelId: membership.funnelId,
    });
  }
  return notices;
}

/**
 * Conversas que esperam resposta.
 *
 * Conversa adiada só volta ao feed quando o adiamento termina — adiar é uma
 * decisão do usuário, e ignorá-la seria devolver o mesmo aviso que ele acabou
 * de tirar da frente.
 */
export function threadNotices(input: FeedInput): Notice[] {
  const threads = input.threads ?? [];
  if (threads.length === 0) return [];
  const byLead = new Map(input.leads.map((lead) => [lead.id, lead]));
  const nowMs = Date.parse(`${input.today}T23:59:59Z`);
  const notices: Notice[] = [];
  for (const thread of threads) {
    if (thread.state === "resolved") continue;
    if (!thread.needsReply) continue;
    if (thread.state === "snoozed" && thread.snoozedUntil) {
      const until = Date.parse(thread.snoozedUntil);
      if (!Number.isNaN(until) && until > nowMs) continue;
    }
    const lead = thread.leadId ? byLead.get(thread.leadId) : undefined;
    if (lead?.archived) continue;
    const inbound = thread.lastInboundAt ?? thread.lastMessageAt;
    notices.push({
      key: `mensagem-responder:${thread.id}:${inbound}`,
      category: "mensagem",
      severity: "atencao",
      title: "Conversa aguardando resposta",
      detail: `${thread.subject || "Sem assunto"} · ${
        thread.leadId ? leadLabel(lead) : thread.counterpartAddress
      }`,
      occurredAt: inbound,
      actionable: true,
      leadId: thread.leadId,
      funnelId: thread.funnelId,
      threadId: thread.id,
    });
  }
  return notices;
}

/**
 * Incidentes de automação em aberto.
 *
 * A causa sanitizada é o texto do aviso: incidente nunca carrega segredo,
 * cabeçalho técnico cru ou conteúdo de mensagem (§27.7).
 */
export function incidentNotices(input: FeedInput): Notice[] {
  const incidents = input.incidents ?? [];
  const notices: Notice[] = [];
  for (const incident of incidents) {
    if (incident.status === "resolved") continue;
    notices.push({
      key: `automacao:${incident.id}`,
      category: "automacao",
      severity:
        incident.severity === "critical" || incident.severity === "error"
          ? "critico"
          : incident.severity === "warning"
            ? "atencao"
            : "informativo",
      title: `Automação: ${incident.code}`,
      detail: `${incident.sanitizedCause} · ${incident.impact}`,
      occurredAt: incident.lastSeenAt,
      actionable: incident.status === "open",
      incidentId: incident.id,
    });
  }
  return notices;
}

/** Janela dos acontecimentos que a caixa mostra, em dias civis. */
export const EVENT_FEED_DAYS = 7;

/**
 * Rótulo e categoria de cada acontecimento que vira aviso.
 *
 * É uma lista fechada de propósito: a jornada registra muito mais coisa do que
 * merece interromper alguém. O que entra aqui é o que muda o estado comercial
 * ou chegou de fora — o resto continua só na jornada do lead.
 */
const EVENT_NOTICES: Record<
  string,
  { title: string; category: NoticeCategory; say: (event: ActivityEvent) => string }
> = {
  lead_created: {
    title: "Lead adquirido",
    category: "lead",
    say: () => "Cadastro criado e disponível para o primeiro contato.",
  },
  lead_converted: {
    title: "Contrato fechado",
    category: "financeiro",
    say: () => "Primeiro recebimento registrado: a venda está fechada.",
  },
  lead_lost: { title: "Lead perdido", category: "lead", say: () => "Negociação encerrada." },
  lead_reactivated: { title: "Lead reativado", category: "lead", say: () => "Negociação retomada." },
  stage_changed: {
    title: "Etapa atualizada",
    category: "lead",
    say: (event) => `Avançou para ${event.title}`,
  },
  contract_created: { title: "Contrato criado", category: "financeiro", say: (event) => event.title },
  receipt_registered: {
    title: "Recebimento registrado",
    category: "financeiro",
    say: (event) => event.title,
  },
  meeting_completed: { title: "Reunião realizada", category: "lead", say: (event) => event.title },
};

/**
 * Acontecimentos recentes da operação.
 *
 * Eles não pedem ação — ninguém "resolve" um lead que entrou — e por isso
 * nascem informativos e ficam na linha do tempo, separados das pendências.
 * Sem eles a caixa só teria o que está atrasado, e a pergunta "o que aconteceu
 * hoje?" não teria resposta nenhuma.
 */
export function eventNotices(input: FeedInput): Notice[] {
  const events = input.events ?? [];
  if (events.length === 0) return [];
  const byLead = new Map(input.leads.map((lead) => [lead.id, lead]));
  const notices: Notice[] = [];
  for (const event of events) {
    const kind = EVENT_NOTICES[event.type];
    if (!kind) continue;
    const day = civilDate(event.occurredAt, input.timeZone);
    if (!day) continue;
    const distance = daysBetween(input.today, day);
    // Futuro não é acontecimento, e o passado tem um horizonte: a caixa é da
    // operação de agora, não um segundo histórico do lead.
    if (distance > 0 || distance < -EVENT_FEED_DAYS) continue;
    const lead = byLead.get(event.leadId);
    if (lead?.archived) continue;
    notices.push({
      key: `acontecimento:${event.id}`,
      category: kind.category,
      severity: "informativo",
      title: kind.title,
      // O texto do evento quando ele existe; senão, a frase do tipo. Repetir o
      // título do aviso na descrição não diz nada a mais a quem lê.
      detail: [leadLabel(lead), event.description || kind.say(event)].filter(Boolean).join(" · "),
      occurredAt: event.occurredAt,
      actionable: false,
      leadId: event.leadId,
      funnelId: input.memberships.find((item) => item.leadId === event.leadId)?.funnelId,
    });
  }
  return notices;
}

/** Tamanho legível do arquivo; o protótipo mostra "2,4 MB" ao lado do nome. */
function fileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/**
 * Anexos recebidos há pouco.
 *
 * Um arquivo que chega é notícia — foi alguém de fora mandando material — mas
 * não é pendência: ninguém "resolve" um anexo. Por isso ele nasce informativo,
 * com o mesmo horizonte dos acontecimentos, e o nome do arquivo é o título,
 * como no protótipo.
 */
export function attachmentNotices(input: FeedInput): Notice[] {
  const attachments = input.attachments ?? [];
  if (attachments.length === 0) return [];
  const byLead = new Map(input.leads.map((lead) => [lead.id, lead]));
  const funnels = funnelIndex(input);
  const notices: Notice[] = [];
  for (const attachment of attachments) {
    const day = civilDate(attachment.createdAt, input.timeZone);
    if (!day) continue;
    const distance = daysBetween(input.today, day);
    if (distance > 0 || distance < -EVENT_FEED_DAYS) continue;
    const lead = byLead.get(attachment.leadId);
    if (lead?.archived) continue;
    notices.push({
      key: `anexo:${attachment.id}`,
      category: "anexo",
      severity: "informativo",
      title: attachment.name,
      detail: [leadLabel(lead), fileSize(attachment.size)].filter(Boolean).join(" · "),
      occurredAt: attachment.createdAt,
      actionable: false,
      leadId: attachment.leadId,
      funnelId: funnels.get(attachment.leadId),
    });
  }
  return notices;
}

/**
 * Ritmo contra a meta do período.
 *
 * É a projeção que o protótipo mostra na caixa "CosmosAI": quanto falta, em
 * quantos dias, e onde o ritmo atual fecha. Não há modelo nenhum aqui — é a
 * receita já recebida dividida pelos dias corridos e reprojetada até o fim do
 * período, e o detalhe do aviso diz isso. O aviso some sozinho quando o ritmo
 * alcança a meta, e por isso nasce informativo: não existe o que "resolver".
 *
 * A chave não carrega o dia. Um aviso que voltasse a ser não lido a cada manhã
 * treinaria a pessoa a ignorar a trilha inteira.
 */
export function goalPaceNotices(input: FeedInput): Notice[] {
  const goals = input.revenueGoals ?? [];
  const goal = goals.find((item) => input.today >= item.periodStart && input.today <= item.periodEnd);
  if (!goal || goal.amountCents <= 0) return [];

  const elapsed = daysBetween(goal.periodStart, input.today) + 1;
  const total = daysBetween(goal.periodStart, goal.periodEnd) + 1;
  if (elapsed <= 0 || total <= 0) return [];

  const achieved = (input.receipts ?? []).reduce(
    (sum, receipt) =>
      receipt.receivedOn >= goal.periodStart && receipt.receivedOn <= goal.periodEnd
        ? sum + receipt.amountCents
        : sum,
    0,
  );
  const projected = Math.round((achieved / elapsed) * total);
  if (projected >= goal.amountCents) return [];

  const share = Math.round((achieved / goal.amountCents) * 100);
  const pace = Math.round((projected / goal.amountCents) * 100);
  const missing = goal.amountCents - achieved;
  const left = total - elapsed;
  return [
    {
      key: `meta-ritmo:${goal.id}`,
      category: "ia",
      severity: "atencao",
      title: `Meta do trimestre a ${share}%`,
      detail: `Faltam ${currency(missing)} em ${left} ${left === 1 ? "dia" : "dias"} — ritmo atual fecha em ${pace}%`,
      occurredAt: `${input.today}T12:00:00.000Z`,
      actionable: false,
    },
  ];
}

const RULES = [
  taskNotices,
  invoiceNotices,
  coolingLeadNotices,
  threadNotices,
  incidentNotices,
  eventNotices,
  attachmentNotices,
  goalPaceNotices,
] as const;

/**
 * Monta o feed aplicando todas as regras, anexando o estado persistido e
 * ordenando por gravidade e depois por data decrescente.
 *
 * Categoria silenciada some do feed, mas o estado gravado dela permanece: voltar
 * a ativar não transforma um aviso já lido em novidade.
 */
export function buildNoticeFeed(input: FeedInput): Notice[] {
  const muted = new Set(input.mutedCategories ?? []);
  const states = new Map((input.states ?? []).map((state) => [state.noticeKey, state]));
  const seen = new Set<string>();
  const notices: Notice[] = [];
  for (const rule of RULES) {
    for (const notice of rule(input)) {
      if (muted.has(notice.category)) continue;
      if (seen.has(notice.key)) continue;
      seen.add(notice.key);
      const state = states.get(notice.key);
      if (state?.resolvedAt && !input.includeResolved) continue;
      notices.push({ ...notice, readAt: state?.readAt, resolvedAt: state?.resolvedAt });
    }
  }
  return notices.sort((left, right) => {
    const bySeverity = SEVERITY_WEIGHT[left.severity] - SEVERITY_WEIGHT[right.severity];
    if (bySeverity !== 0) return bySeverity;
    return right.occurredAt.localeCompare(left.occurredAt);
  });
}

/** Quantos avisos pendentes não foram lidos; é o número do sino. */
export function unreadCount(notices: readonly Notice[]): number {
  return notices.filter((notice) => !notice.readAt && !notice.resolvedAt).length;
}

/** Contagem por categoria, para as abas da central. */
export function countByCategory(notices: readonly Notice[]): Record<NoticeCategory, number> {
  const counts = Object.fromEntries(
    NOTICE_CATEGORIES.map((category) => [category, 0]),
  ) as Record<NoticeCategory, number>;
  for (const notice of notices) {
    if (notice.resolvedAt) continue;
    counts[notice.category] += 1;
  }
  return counts;
}
